import useBreakpoint from '@/main/lib/hooks/useBreakpoint';
import getAllArticles from 'api/Article/getAllArticles';
import PaidPlacement from 'components/PaidPlacement';
import EDUCATION_ADS_TEST_IDS from 'constants/testsIds/educationAds';
import useHeaderCoordinates from 'hooks/useHeaderCoordinates';
import type { Article, ArticlePreview, ArticleSlug } from 'interfaces/content/articles/Articles';
import type { EduAdWigetsByPosition } from 'interfaces/EduRanking';
import type { GetStaticPaths, NextPage } from 'next';
import dynamic from 'next/dynamic';
import { ArticlePage } from 'pages/Article/V2/ArticlePage';
import type { Params } from 'utils/miscUtils/articlePageHelpers';
import { getArticleStaticProps } from 'utils/miscUtils/getArticleStaticProps';
import setArticleEmbeds from 'utils/miscUtils/setArticleEmbeds';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

const Outbrain = dynamic(() => import('components/Outbrain'), {
  ssr: false,
});

interface ArticleProps {
  article: Article;
  subdomain: string;
  latestPosts: ArticlePreview[];
  mostPopular: ArticlePreview[];
  noOutbrain: boolean;
  noRecirculationModules: boolean;
  noTrinityAds: boolean;
  adWidgets: EduAdWigetsByPosition;
}

const ArticleNextPage: NextPage<ArticleProps> = ({
  article,
  mostPopular,
  subdomain,
  latestPosts,
  noOutbrain,
  noRecirculationModules,
  noTrinityAds,
  adWidgets,
}: ArticleProps) => {
  const { bottom } = useHeaderCoordinates();
  const footerComponents = [];
  const { userFlags: userVariants } = useOptimizely();
  const { articleStickyFooter } = userVariants as { articleStickyFooter: { enabled: boolean } };
  const hasStickyFooter = articleStickyFooter?.enabled;
  const isMobile = useBreakpoint('mmMax');
  const suppressSTNVideo = hasStickyFooter && isMobile;

  if (!noOutbrain) {
    footerComponents.push(
      <Outbrain
        key='1'
        placement='footer'
        section={article.primarySection.name}
        permalink={article.url}
      />,
    );
  }
  const postId = article.postId.toString();
  return (
    <ArticlePage
      article={article}
      subdomain={subdomain}
      stickyTop={bottom}
      footer={footerComponents}
      embeds={setArticleEmbeds(article, suppressSTNVideo)}
      lastPWithoutAds={0}
      latestPosts={{
        posts: latestPosts,
      }}
      mostPopular={{
        posts: mostPopular,
      }}
      noRecirculationModules={noRecirculationModules}
      noTrinityAds={noTrinityAds}
      headerAds={adWidgets?.header?.map((ad) => (
        <PaidPlacement
          dataCy={EDUCATION_ADS_TEST_IDS.ARTICLE_HEADER}
          key={`pp-${ad.order}`}
          adContent={ad.content}
          adTag={ad.adTag}
          position='header'
          entityId={postId}
          className='sponsored-ad-tag'
        />
      ))}
    />
  );
};

export default ArticleNextPage;

interface Props {
  params: {
    slug: string;
    param: string;
    month: string;
    day: string;
  };
}

export const getStaticProps = async (context) => {
  return getArticleStaticProps(context);
};

export const getStaticPaths: GetStaticPaths<Params> = async () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: {
            day: '23',
            month: '02',
            param: '2022',
            slug: 'equal-pay-paternity-leave-workplace-gender-equity',
          },
        },
      ],
    };
  }

  const articles: ArticleSlug[] = await getAllArticles();
  const paths: Props[] = articles
    .filter((article) => !article.uri.match(/crypto|europe|recommends|well/g))
    .map((article) => ({
      params: {
        day: article.date.substring(8, 10),
        month: article.date.substring(5, 7),
        param: article.date.substring(0, 4),
        slug: article.slug,
      },
    }));

  return {
    fallback: 'blocking',
    paths,
  };
};
